import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEY } from '../../constants';
import { createMessagesSlice } from './messagesSlice';
import { createSelectionSlice } from './selectionSlice';
import { createStreamingSlice } from './streamingSlice';
import { createTabsSlice } from './tabsSlice';
import { createUiSlice } from './uiSlice';
import type { AgentForgeStore } from './types';
import { INITIAL_STATE } from './types';

export const useAgentForgeStore = create<AgentForgeStore>()(
  persist(
    (...a) => ({
      ...INITIAL_STATE,
      ...createTabsSlice(...a),
      ...createUiSlice(...a),
      ...createMessagesSlice(...a),
      ...createStreamingSlice(...a),
      ...createSelectionSlice(...a),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        openTabs: s.openTabs,
        activeSessionId: s.activeSessionId,
        mode: s.mode,
        historyOpen: s.historyOpen,
        messagesBySession: s.messagesBySession,
        selectionByBlock: s.selectionByBlock,
        draftBySession: s.draftBySession,
      }),
      // A freshly-loaded page owns no live stream, so a persisted
      // 'streaming' status is by definition stale (tab closed or page
      // reloaded mid-turn). Left as-is it wedges the session: the hydrate
      // hook skips "mid-stream" and useChatSubmit refuses to send. If the
      // turn really is still running server-side, its WS events re-create
      // the draft and re-gate sends within moments.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AgentForgeStore>;
        const openTabs = (p.openTabs ?? []).map((t) =>
          t.status === 'streaming' ? { ...t, status: 'done' as const } : t
        );
        return { ...current, ...p, openTabs };
      },
    }
  )
);
